import { FunctionComponent, useContext } from 'react'
import styled, { css } from 'styled-components'
import contextSportsHeader, {
  SportsHeaderContextType,
} from '@contexts/sportsHeader'
import { ScoreProps } from '@widgets/Teaser/sportsHeader/types'
import { ViewportTypeValues } from '@contexts/viewport'
import { computeTeaserWidth } from '@widgets/Teaser/utils'
import { desktopCSS, tabletCSS, mobileCSS } from '@measures/responsive'

type StyledScoreProps = Pick<
  SportsHeaderContextType,
  'teaserFlex' | 'teaserType' | 'isInMegaModule'
>

const { SportsHeaderContext } = contextSportsHeader

const StyledScore = styled.div<StyledScoreProps>`
  ${({
    theme: {
      fonts: { goodOT: goodOTFontName },
      colors: { white },
    },
    teaserFlex,
    teaserType,
    isInMegaModule,
  }) => {
    let fontSizeDesktop
    let lineHeightDesktop
    const desktopTeaserFlex = teaserFlex['desktop']
    const teaserWidthDesktop = computeTeaserWidth({
      flexValue: desktopTeaserFlex,
      viewportType: ViewportTypeValues.DESKTOP,
    })

    let fontSizeTablet
    let lineHeightTablet
    const tabletTeaserFlex = teaserFlex['tablet']
    const teaserWidthTablet = computeTeaserWidth({
      flexValue: tabletTeaserFlex,
      viewportType: ViewportTypeValues.TABLET,
    })

    let fontSizeMobile
    let lineHeightMobile

    if (
      teaserType === 'listitem' ||
      (teaserType === 'standard' && isInMegaModule)
    ) {
      fontSizeDesktop = '22px'
      lineHeightDesktop = 1.2083
      fontSizeTablet = '22px'
      lineHeightTablet = 1.2083
    } else {
      if (desktopTeaserFlex === '6') {
        fontSizeDesktop = `${0.03769 * teaserWidthDesktop}px`
        lineHeightDesktop = 1.21
      } else if (desktopTeaserFlex === '4') {
        fontSizeDesktop = `${0.05722 * teaserWidthDesktop}px`
        lineHeightDesktop = 1.21
      } else if (desktopTeaserFlex === '3') {
        fontSizeDesktop = `${0.065 * teaserWidthDesktop}px`
        lineHeightDesktop = 1.1875
      } else if (desktopTeaserFlex === '2') {
        fontSizeDesktop = `${0.075 * teaserWidthDesktop}px`
        lineHeightDesktop = 1.2083
      }

      if (tabletTeaserFlex === '6') {
        fontSizeTablet = `${0.05722 * teaserWidthTablet}px`
        lineHeightTablet = 1.21
      } else if (tabletTeaserFlex === '3') {
        fontSizeTablet = `${0.065 * teaserWidthTablet}px`
        lineHeightTablet = 1.1875
      }
    }

    if (['standard', 'listitem'].includes(teaserType)) {
      fontSizeMobile = '22px'
      lineHeightMobile = 1.2083
    } else {
      fontSizeMobile = '7.4667vw'
      lineHeightMobile = 1.214
    }

    return css`
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      color: ${white};
      font-family: ${goodOTFontName}, 'Arial Narrow', Impact;
      font-weight: 900;
      padding: 0 10px;
      ${desktopCSS(css`
        font-size: ${fontSizeDesktop};
        line-height: ${lineHeightDesktop};
      `)}

      ${tabletCSS(css`
        font-size: ${fontSizeTablet};
        line-height: ${lineHeightTablet};
      `)}

      ${mobileCSS(css`
        font-size: ${fontSizeMobile};
        line-height: ${lineHeightMobile};
      `)}
    `
  }}
`

const Score: FunctionComponent<ScoreProps> = ({
  homeTeam: { score: homeTeamScore },
  awayTeam: { score: awayTeamScore },
  children,
}) => {
  const { teaserFlex, teaserType, isInMegaModule } =
    useContext(SportsHeaderContext)

  return (
    <StyledScore
      teaserFlex={teaserFlex}
      teaserType={teaserType}
      isInMegaModule={isInMegaModule}>
      {homeTeamScore}
      {children}
      {awayTeamScore}
    </StyledScore>
  )
}

export default Score
